import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { ErrorBanner } from '../components/ui';
import { IconArrowRight, IconLock, IconMail } from '../components/icons';
import { LoginHero } from './LoginPage';

type Step = 'email' | 'code' | 'password';

const MIN_PASSWORD_LENGTH = 10; // matches the server rule

/**
 * Forgot password, in three steps on one route: email → emailed 6-digit code →
 * new password. Held in component state rather than the URL, because the reset
 * token from step two is a credential and must not land in history.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice('');
    try {
      await action();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const sendCode = () =>
    run(async () => {
      const res = await api<{ expires_in_minutes: number; resend_after_seconds: number }>(
        '/auth/password/forgot',
        { method: 'POST', anonymous: true, body: { email: email.trim() } },
      );
      setResendIn(res.resend_after_seconds);
      setCode('');
      setStep('code');
      setNotice(`We sent a 6-digit code to ${email.trim()}. It expires in ${res.expires_in_minutes} minutes.`);
    });

  function submitEmail(e: FormEvent) {
    e.preventDefault();
    void sendCode();
  }

  function submitCode(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const res = await api<{ reset_token: string }>('/auth/password/verify-code', {
        method: 'POST',
        anonymous: true,
        body: { email: email.trim(), code },
      });
      setResetToken(res.reset_token);
      setStep('password');
    });
  }

  function submitPassword(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(new Error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`));
      return;
    }
    if (password !== confirm) {
      setError(new Error('The two passwords do not match.'));
      return;
    }
    void run(async () => {
      await api<void>('/auth/password/reset', {
        method: 'POST',
        anonymous: true,
        body: { email: email.trim(), reset_token: resetToken, new_password: password },
      });
      navigate('/login', { replace: true, state: { passwordReset: true } });
    });
  }

  const heads: Record<Step, { title: string; sub: string }> = {
    email: { title: 'Forgot password', sub: 'Enter the email address registered with your account and we will send you a code.' },
    code: { title: 'Check your email', sub: 'Enter the 6-digit code we emailed you.' },
    password: { title: 'Set a new password', sub: `Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.` },
  };

  return (
    <div className="login-shell">
      <LoginHero />

      <div className="login-panel">
        <div className="login-form-wrap">
          <div className="login-form-head">
            <h2>{heads[step].title}</h2>
            <p>{heads[step].sub}</p>
          </div>

          {notice && !error && <div className="banner banner-info" role="status">{notice}</div>}
          <ErrorBanner error={error} />

          {step === 'email' && (
            <form onSubmit={submitEmail}>
              <div className="field">
                <label htmlFor="email">Email</label>
                <div className="input-icon">
                  <IconMail width={16} height={16} />
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    autoFocus
                    placeholder="you@organisation.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>
              <button className="btn btn-primary login-submit" disabled={busy}>
                {busy ? <span className="spinner" /> : <>Send code <IconArrowRight width={15} height={15} /></>}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={submitCode}>
              <div className="field">
                <label htmlFor="code">Verification code</label>
                <input
                  id="code"
                  className="code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  maxLength={6}
                  pattern="\d{6}"
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              <button className="btn btn-primary login-submit" disabled={busy || code.length !== 6}>
                {busy ? <span className="spinner" /> : <>Verify code <IconArrowRight width={15} height={15} /></>}
              </button>
              <div className="login-alt">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => { setStep('email'); setError(null); setNotice(''); }}
                >
                  Use a different email
                </button>
                <button type="button" className="link-button" disabled={busy || resendIn > 0} onClick={() => void sendCode()}>
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={submitPassword}>
              {/* Lets the browser's password manager store the new password against the right account. */}
              <input type="email" autoComplete="username" value={email} readOnly hidden />
              <div className="field">
                <label htmlFor="new-password">New password</label>
                <div className="input-icon">
                  <IconLock width={16} height={16} />
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    autoFocus
                    minLength={MIN_PASSWORD_LENGTH}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="field-hint">At least {MIN_PASSWORD_LENGTH} characters.</div>
              </div>
              <div className="field">
                <label htmlFor="confirm-password">Confirm new password</label>
                <div className="input-icon">
                  <IconLock width={16} height={16} />
                  <input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>
              </div>
              <button className="btn btn-primary login-submit" disabled={busy}>
                {busy ? <span className="spinner" /> : <>Reset password <IconArrowRight width={15} height={15} /></>}
              </button>
            </form>
          )}

          <div className="login-alt">
            <Link to="/login" className="label-link">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
